import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type OrgWithStats,
  type DeviceWithStatus,
  enrichDeviceWithStatus,
} from '../api/client'
import { getDefaultUniverse } from '../api/universeGenerator'
import { StatusIndicator } from '../components/StatusIndicator'
import { formatLastSeen } from '../api/client'

function getAlertReason(device: DeviceWithStatus): string {
  if (device.status === 'critical') {
    return 'Not seen for over 24 hours'
  }
  const parts: string[] = []
  if (!device.isCurrentVersion) {
    parts.push(`Running outdated version v${device.currentVersion}`)
  }
  // Check staleness (1-24h range = warning)
  const lastSeenStr = device.lastSeen ?? device.lastCheckIn
  const hoursSince = (Date.now() - new Date(lastSeenStr).getTime()) / (1000 * 60 * 60)
  if (hoursSince > 1) {
    parts.push(`Last seen ${formatLastSeen(lastSeenStr)}`)
  }
  return parts.join('; ') || 'Needs attention'
}

export default function AlertsPage({ organizations }: { organizations: OrgWithStats[] }) {
  const navigate = useNavigate()

  const alerts = useMemo(() => {
    const universe = getDefaultUniverse()
    const orgMap = new Map(organizations.map(o => [o.orgId, o.name]))

    const allDevices = universe.devices
      .map(enrichDeviceWithStatus)
      .filter(d => d.status === 'critical' || d.status === 'warning')
      .map(d => ({
        ...d,
        orgName: orgMap.get(d.orgId) ?? d.orgId,
        reason: getAlertReason(d),
      }))

    // Critical first, then warning; within each group sort by last seen (oldest first)
    const statusOrder = { critical: 0, warning: 1, healthy: 2 }
    allDevices.sort((a, b) => {
      const so = statusOrder[a.status] - statusOrder[b.status]
      if (so !== 0) return so
      const aTime = new Date(a.lastSeen ?? a.lastCheckIn).getTime()
      const bTime = new Date(b.lastSeen ?? b.lastCheckIn).getTime()
      return aTime - bTime
    })

    return allDevices
  }, [organizations])

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white tracking-tight">Alerts</h1>
        <p className="text-slate-500 text-sm mt-1">
          Devices that need attention across all organizations.
        </p>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-8 text-center">
          <div className="text-emerald-400 text-lg font-medium mb-1">All devices are healthy</div>
          <p className="text-slate-500 text-sm">No devices currently need attention.</p>
        </div>
      ) : (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-800/50">
            {alerts.map(device => (
              <button
                key={device.clientId}
                onClick={() => navigate(`/orgs/${device.orgId}/devices/${device.clientId}`)}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-800/40 transition-colors text-left"
              >
                <StatusIndicator status={device.status} pulse={device.status === 'critical'} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white truncate">
                      {device.assetId || device.userName || device.clientId}
                    </span>
                    <span className="text-xs text-slate-600 truncate">{device.orgName}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{device.reason}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    device.status === 'critical'
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-amber-500/10 text-amber-400'
                  }`}>
                    {device.status}
                  </span>
                  <div className="text-xs text-slate-600 mt-1">
                    {formatLastSeen(device.lastSeen ?? device.lastCheckIn)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
